import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { AlertCircle, Loader2 } from 'lucide-react';
import Loader from '../../../components/Loader';
import { sentenceCase } from '../../../utils/miscFunctions';

export interface IPremiumFinancingMonthlyTrendItem {
  month: string;
  year: number;
  count: number;
}

interface PremiumFinancingMonthlyTrendsChartProps {
  data: IPremiumFinancingMonthlyTrendItem[];
  title?: string;
  description?: string;
  isLoading?: boolean;
  error?: string | null;
}

const PremiumFinancingMonthlyTrendsChart: React.FC<PremiumFinancingMonthlyTrendsChartProps> = ({
  data,
  title = 'Monthly Trends',
  description = 'Monthly count data',
  isLoading = false,
  error = null,
}) => {
  // Calculate average count
  const avgCount = data.length > 0 ? data.reduce((sum, item) => sum + item.count, 0) / data.length : 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white shadow-sm border border-gray-200 p-6 rounded-2xl dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">{title}</h2>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
        <div className="h-80 flex flex-col items-center justify-center space-y-4">
          <Loader message="Loading chart data..." />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">{title}</h2>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
        <div className="h-80 flex flex-col items-center justify-center space-y-4 text-center p-6">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">Unable to load data</h3>
          <p className="text-sm text-gray-500 max-w-md">
            {error || 'An error occurred while loading the chart data. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">{title}</h2>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
        <div className="h-80 flex flex-col items-center justify-center space-y-4 text-center p-6">
          <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center dark:bg-darkBg dark:border dark:border-darkBg-border">
            <svg
              className="h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">No data available</h3>
          <p className="text-sm text-gray-500 max-w-md">
            There is no data to display for the selected time period.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 dark:bg-darkBg dark:border dark:border-darkBg-border">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">{title}</h2>
        <p className="text-sm text-gray-600">{description}</p>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <defs>
              <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12 }}
              padding={{ left: 10, right: 10 }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={(value: number) => [value, 'Count']}
              labelFormatter={(label, payload) => {
                const item = payload[0]?.payload;
                return item ? `${sentenceCase(item.month)} ${item.year}` : label;
              }}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '4px',
                padding: '10px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
            />
            <Bar
              dataKey="count"
              name="Premium Financing Count"
              fill="#FFBB28"
              radius={[4, 4, 0, 0]}
              barSize={30}
              fillOpacity={0.8}
            />
            <Legend 
              verticalAlign="bottom"
              height={36}
              formatter={(value) => <span className="text-sm text-gray-700">{ sentenceCase(value)}</span>}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PremiumFinancingMonthlyTrendsChart;
